// Review composer — Design Pack §7.3.
//
// Five categories on a 1–5 scale (skippable/null), public free text, an
// optional flag-to-trust&safety private note. Save Draft + Submit Review.
// On submit, the API runs the mutual-release pipeline and the response tells
// us whether the review went live alongside the counterparty's.

import {
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { csrfHeaders } from '../../lib/auth-context';

export const Route = createFileRoute('/exchanges/$id_/review')({
  component: ReviewComposer,
});

type Score = 1 | 2 | 3 | 4 | 5 | null;

type ScoresState = {
  communication: Score;
  hospitality: Score;
  performance: Score;
  safety: Score;
  exchange_respected: Score;
};

type ReviewDto = {
  id: string;
  state: 'drafted' | 'submitted' | 'released' | 'closed_unreviewed';
  scores: {
    communication?: number | null;
    hospitality?: number | null;
    performance?: number | null;
    safety?: number | null;
    exchangeRespected?: number | null;
  };
  free_text_public: string | null;
  private_note_to_team: string | null;
};

const EMPTY_SCORES: ScoresState = {
  communication: null,
  hospitality: null,
  performance: null,
  safety: null,
  exchange_respected: null,
};

const CATEGORY_ROWS: ReadonlyArray<{
  key: keyof ScoresState;
  label: string;
  hint?: string;
}> = [
  { key: 'communication', label: 'Communication' },
  { key: 'hospitality', label: 'Hospitality' },
  {
    key: 'performance',
    label: 'What they brought',
    hint: 'the set, the show, the work. Skip if not applicable',
  },
  { key: 'safety', label: 'Safety' },
  { key: 'exchange_respected', label: 'Exchange respected' },
];

function ReviewComposer() {
  const { id: exchangeId } = Route.useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scores, setScores] = useState<ScoresState>(EMPTY_SCORES);
  const [freeText, setFreeText] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [includePrivateNote, setIncludePrivateNote] = useState(false);
  const [reviewState, setReviewState] = useState<ReviewDto['state'] | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Initial load — fetch existing draft if any.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/reviews/me/exchange/${exchangeId}/draft`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 404) {
            setErrorMsg('We could not find that exchange.');
          } else if (res.status === 403) {
            setErrorMsg('You are not on this exchange.');
          } else {
            setErrorMsg('Something went wrong loading your draft.');
          }
          setLoading(false);
          return;
        }
        const data = (await res.json()) as { review: ReviewDto | null };
        if (data.review) {
          const s = data.review.scores;
          setScores({
            communication: clampScore(s.communication ?? null),
            hospitality: clampScore(s.hospitality ?? null),
            performance: clampScore(s.performance ?? null),
            safety: clampScore(s.safety ?? null),
            exchange_respected: clampScore(s.exchangeRespected ?? null),
          });
          setFreeText(data.review.free_text_public ?? '');
          if ((data.review.private_note_to_team ?? '').length > 0) {
            setPrivateNote(data.review.private_note_to_team ?? '');
            setIncludePrivateNote(true);
          }
          setReviewState(data.review.state);
        }
      } catch {
        if (!cancelled) setErrorMsg('Something went wrong loading your draft.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exchangeId]);

  const submitDisabled = useMemo(() => {
    if (submitting || saving) return true;
    if (freeText.trim().length < 20) return true;
    // Allow null scores (skip) per design pack — but require at least one
    // category answered so an empty submission is impossible.
    const anyScored = Object.values(scores).some((v) => typeof v === 'number');
    if (!anyScored) return true;
    return false;
  }, [submitting, saving, freeText, scores]);

  async function handleSaveDraft() {
    setSaving(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`/api/reviews/me/exchange/${exchangeId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          exchange_id: exchangeId,
          scores: scoresToWire(scores),
          ...(freeText.length > 0 ? { free_text_public: freeText } : {}),
          ...(includePrivateNote && privateNote.length > 0
            ? { private_note_to_team: privateNote }
            : {}),
        }),
      });
      if (!res.ok) {
        const j = await safeJson(res);
        setErrorMsg(j?.error?.message ?? 'Save failed.');
        return;
      }
      notifications.show({
        message: 'Draft saved.',
        color: 'gray',
      });
    } catch {
      setErrorMsg('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await fetch(`/api/reviews/me/exchange/${exchangeId}/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          exchange_id: exchangeId,
          scores: scoresToWire(scores),
          free_text_public: freeText,
          ...(includePrivateNote && privateNote.length > 0
            ? { private_note_to_team: privateNote }
            : {}),
        }),
      });
      if (!res.ok) {
        const j = await safeJson(res);
        setErrorMsg(j?.error?.message ?? 'Submit failed.');
        return;
      }
      const data = (await res.json()) as {
        review: ReviewDto;
        release: { decision: 'both' | 'one' | 'none'; releasedReviewIds: string[] };
      };
      const released = data.release.decision !== 'none';
      notifications.show({
        title: released ? 'Your review is live.' : 'Your review is in.',
        message: released
          ? 'Both reviews went live together.'
          : "We'll let you know when theirs is in too. They go live together.",
      });
      void navigate({ to: '/exchanges/$id', params: { id: exchangeId } });
    } catch {
      setErrorMsg('Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (errorMsg !== null && reviewState === null) {
    return (
      <AppShell tone="form">
        <Alert color="red" title="We hit a snag">
          {errorMsg}
        </Alert>
        <Box mt="md">
          <Anchor component={Link} to="/dashboard">
            Back to dashboard
          </Anchor>
        </Box>
      </AppShell>
    );
  }

  if (reviewState === 'submitted' || reviewState === 'released') {
    return (
      <AppShell tone="form">
        <Stack gap="md">
          <Title order={2}>Your review is in.</Title>
          <Text>
            {reviewState === 'released'
              ? 'Your review is live. Thanks for taking the time.'
              : "We'll let you know when theirs is in too. They go live together."}
          </Text>
          <Anchor href={`/exchanges/${exchangeId}`}>Back to the exchange</Anchor>
        </Stack>
      </AppShell>
    );
  }

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <Box>
          <Title order={2}>Review your exchange</Title>
          <Text c="dimmed" mt="xs">
            Reviews are private until both of you have submitted. After 14 days from
            completion, whichever review is in by then goes live alone.
          </Text>
        </Box>

        <Stack gap="sm">
          <Title order={4}>Categories</Title>
          {CATEGORY_ROWS.map((row) => (
            <Group key={row.key} justify="space-between" wrap="nowrap" gap="md">
              <Box>
                <Text>{row.label}</Text>
                {row.hint !== undefined ? (
                  <Text size="xs" c="dimmed">
                    ({row.hint})
                  </Text>
                ) : null}
              </Box>
              <StarRow
                value={scores[row.key]}
                onChange={(v) => setScores((s) => ({ ...s, [row.key]: v }))}
              />
            </Group>
          ))}
        </Stack>

        <Box>
          <Title order={4}>Your review</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Tell hosts what to expect from a stay with you. Be specific.
          </Text>
          <Textarea
            mt="xs"
            minRows={6}
            maxLength={4000}
            value={freeText}
            onChange={(e) => setFreeText(e.currentTarget.value)}
            placeholder="What stood out? What should the next artist (or host) know?"
          />
          <Text size="xs" c="dimmed" mt={4}>
            At least a sentence (20 characters).
          </Text>
        </Box>

        <Box>
          <Checkbox
            label="I want to flag something to the trust & safety team"
            description="Optional, private. Never visible to the other party."
            checked={includePrivateNote}
            onChange={(e) => setIncludePrivateNote(e.currentTarget.checked)}
          />
          {includePrivateNote ? (
            <Textarea
              mt="xs"
              minRows={3}
              maxLength={2000}
              value={privateNote}
              onChange={(e) => setPrivateNote(e.currentTarget.value)}
              placeholder="What do you want our team to know?"
            />
          ) : null}
        </Box>

        {errorMsg !== null ? (
          <Alert color="red" title="We hit a snag">
            {errorMsg}
          </Alert>
        ) : null}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleSaveDraft} loading={saving}>
            Save draft
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled} loading={submitting}>
            Submit review
          </Button>
        </Group>
      </Stack>
    </AppShell>
  );
}

function StarRow({
  value,
  onChange,
}: {
  value: Score;
  onChange: (v: Score) => void;
}) {
  return (
    <Group gap={2} wrap="nowrap">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value !== null && n <= value;
        return (
          <Box
            key={n}
            component="button"
            type="button"
            onClick={() => onChange(value === n ? null : (n as Score))}
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            aria-pressed={value === n}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 6px',
              fontSize: '20px',
              lineHeight: 1,
              color: active ? 'var(--mantine-color-yellow-6)' : 'var(--mantine-color-gray-4)',
            }}
          >
            {active ? '★' : '☆'}
          </Box>
        );
      })}
      {value !== null ? (
        <Anchor
          component="button"
          type="button"
          size="xs"
          ml={6}
          onClick={() => onChange(null)}
        >
          Skip
        </Anchor>
      ) : null}
    </Group>
  );
}

function clampScore(v: number | null): Score {
  if (v === null) return null;
  if (v < 1 || v > 5 || !Number.isInteger(v)) return null;
  return v as Score;
}

function scoresToWire(s: ScoresState): {
  communication: number | null;
  hospitality: number | null;
  performance: number | null;
  safety: number | null;
  exchange_respected: number | null;
} {
  return {
    communication: s.communication,
    hospitality: s.hospitality,
    performance: s.performance,
    safety: s.safety,
    exchange_respected: s.exchange_respected,
  };
}

async function safeJson(res: Response): Promise<{ error?: { message?: string } } | null> {
  try {
    return (await res.json()) as { error?: { message?: string } };
  } catch {
    return null;
  }
}
